import React, {
  useCallback,
  type FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import _ from 'lodash';
import {
  widthValueOnPreviewChange,
  topBarClick,
} from '@wix/bi-logger-editor/v2';
import { previewModeSliderMaxValueChangeSrc38Evid1533 } from '@wix/bi-logger-editor-data/v2';
import type { widthValueOnPreviewChangeParams } from '@wix/bi-logger-editor/v2/types';
import styles from './deviceSwitchWithResize.scss';
import * as util from '#packages/util';

import Button from '../topBarButton/topBarButtonWithTooltip';
import TooltipContent from '../tooltipContent';
import connect, {
  type DeviceSwitchWithResizeStateProps,
  type DeviceSwitchWithResizeDispatchProps,
  mapPreviewTypeToBiPreviewMode,
} from './deviceSwitchWithResizeMappers';
import {
  BREAKPOINT_DELTA,
  TABLET_LAPTOP_WIDTH,
  TABLET_WIDTH,
  DESKTOP_WIDTH,
  WIDE_DESKTOP_WIDTH,
  PREVIEW_HORIZONTAL_MARGIN,
  COMMONLY_LARGE_LAPTOP,
  COMMONLY_SMALL_TABLET,
  MOBILE_CANVAS_SIZE,
  DESKTOP_CANVAS_SIZE,
} from '#packages/preview';
import { hooks } from '#packages/util';
import { PopoverMenu, Slider } from '@wix/design-system';
import { ChevronDown } from '@wix/wix-ui-icons-common/classic-editor';
import { LargeDesktop } from '@wix/wix-ui-icons-common';
import * as symbols from '@wix/santa-editor-symbols';
import type { PreviewType } from '#packages/stateManagement';
import { translate } from '#packages/i18n';

function usePrevious<T>(value: T) {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const SLIDER_MARKS: { [key: number]: number } = {
  [MOBILE_CANVAS_SIZE]: MOBILE_CANVAS_SIZE,
  [COMMONLY_SMALL_TABLET]: COMMONLY_SMALL_TABLET,
  [TABLET_WIDTH]: TABLET_WIDTH,
  [DESKTOP_CANVAS_SIZE]: DESKTOP_CANVAS_SIZE,
  [DESKTOP_WIDTH]: DESKTOP_WIDTH,
  [COMMONLY_LARGE_LAPTOP]: COMMONLY_LARGE_LAPTOP,
  [WIDE_DESKTOP_WIDTH]: WIDE_DESKTOP_WIDTH,
};

const calcPreviewWidth = (
  currentWidth: number,
  maxWidth: number,
  baselineWidth: number,
) => {
  if (currentWidth > maxWidth) {
    return maxWidth;
  }

  const BREAKPOINTS = [
    TABLET_WIDTH,
    baselineWidth,
    TABLET_LAPTOP_WIDTH,
    DESKTOP_WIDTH,
    WIDE_DESKTOP_WIDTH,
  ];
  const matchedBreakPoint = BREAKPOINTS.find(
    (bpWidth) => Math.abs(currentWidth - bpWidth) < BREAKPOINT_DELTA,
  );

  return matchedBreakPoint ?? currentWidth;
};

interface DeviceDropDownMenuItem {
  previewType?: PreviewType;
  text: string;
  icon: React.ReactElement;
  onClick: () => Promise<void>;
  automationId?: string;
  tooltip?: string;
  biCategoryForDropdown?: string;
}

interface DeviceSwitchWithResizeProps
  extends DeviceSwitchWithResizeStateProps,
    DeviceSwitchWithResizeDispatchProps {}

const DeviceSwitchWithResize: FunctionComponent<
  DeviceSwitchWithResizeProps
> = ({
  previewWidth,
  baselineWidth,
  initialWindowWidth,
  previewType,
  setPreviewWidth,
  initPreview,
  switchToMobile,
  switchToDesktop,
  switchToFullScreen,
  switchToLargeScreen,
  disablePreviewResizeAnimation,
}) => {
  const isDesktop =
    previewType === 'desktop' && previewWidth >= TABLET_LAPTOP_WIDTH;
  const shouldShowSlider =
    previewType != 'fullScreen' && previewType != 'largeScreen';
  const sliderStartValue = useRef(shouldShowSlider ? previewWidth : null);

  const [windowWidth, setWindowWidth] = useState(initialWindowWidth);

  const previousWindowWidth = usePrevious(windowWidth);
  const previousPreviewType = usePrevious(previewType);

  const reportTopBarClick = (category: string) => {
    util.biLogger.report(
      topBarClick({
        isPreview: true,
        origin: 'preview',
        category,
      }),
    );
  };

  const fullSizeBtnClickHandler = async () => {
    switchToFullScreen();
    reportTopBarClick('preview_mode_full_screen');
  };

  const largeScreenBtnClickHandler = async () => {
    await switchToLargeScreen(windowWidth);
    reportTopBarClick('preview_mode_large_desktop');
  };

  const dropDownMenuItems = [
    {
      previewType: 'fullScreen' as PreviewType,
      text: translate('Topbar_Preview_Mode_Fit_Tooltip'),
      icon: (
        <symbols.symbol
          key={`symbol-topBarMaximizeIcon`}
          name={'topBarMaximizeIcon'}
          className={'btn-symbol'}
        />
      ),
      onClick: fullSizeBtnClickHandler,
      automationId: 'top-bar-switch-full-screen',
      biCategoryForDropdown: 'preview_mode_full_screen_dropdown',
    },
    {
      previewType: 'largeScreen' as PreviewType,
      text: translate('Topbar_Preview_Mode_Large_Tooltip'),
      icon: <LargeDesktop className={'btn-symbol'} height={24} width={24} />,
      automationId: 'top-bar-switch-large-screen',
      onClick: largeScreenBtnClickHandler,
      biCategoryForDropdown: 'preview_mode_large_desktop_dropdown',
    },
  ];

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] =
    useState<DeviceDropDownMenuItem>(dropDownMenuItems[0]);

  hooks.useIsMounted(() => {
    initPreview();
  });

  const maxWidth = useMemo(
    () => Math.max(windowWidth - PREVIEW_HORIZONTAL_MARGIN * 2, baselineWidth),
    [windowWidth, baselineWidth],
  );

  const previousMaxWidth = usePrevious(maxWidth);

  const sendSliderMaxValueChangeBi = useCallback(() => {
    if (!!previousMaxWidth && previousMaxWidth !== maxWidth) {
      util.biLogger.report(
        previewModeSliderMaxValueChangeSrc38Evid1533({
          SliderNewValue: maxWidth,
          SliderPreviousValue: previousMaxWidth,
          viewportWidthOld: windowWidth,
          viewportWidthNew: previousWindowWidth,
          preview_mode: mapPreviewTypeToBiPreviewMode(
            previewType,
            previewWidth,
          ),
          isSnappingPoint: !!SLIDER_MARKS?.[previewWidth],
          siteFrameWidth: previewWidth,
        }),
      );
    }
  }, [
    maxWidth,
    previewType,
    previewWidth,
    windowWidth,
    previousMaxWidth,
    previousWindowWidth,
  ]);

  const debounceSliderMaxValueChangeBi = useMemo(
    () => _.debounce(sendSliderMaxValueChangeBi, 500),
    [sendSliderMaxValueChangeBi],
  );

  useEffect(() => {
    debounceSliderMaxValueChangeBi();
  }, [maxWidth, debounceSliderMaxValueChangeBi]);

  const sendSliderValueOnPreviewChangeBi = useCallback(
    (params: widthValueOnPreviewChangeParams) => {
      util.biLogger.report(widthValueOnPreviewChange(params));
    },
    [],
  );

  const reportPreviewWidthChange = useCallback(
    (
      currentWidth: number,
      previousPreviewWidth: number,
      changeOrigin: 'slider' | 'desktop_button' | 'mobile_button',
    ) => {
      const currentPreviewMode = mapPreviewTypeToBiPreviewMode(
        previewType,
        currentWidth,
      );
      const prevPreviewMode = mapPreviewTypeToBiPreviewMode(
        previousPreviewType,
        previousPreviewWidth,
      );
      const isPreviewModeChanged = currentPreviewMode !== prevPreviewMode;
      sendSliderValueOnPreviewChangeBi({
        selected_value: String(currentWidth),
        previous_value: String(previousPreviewWidth),
        value_change_origin: changeOrigin,
        preview_mode: currentPreviewMode,
        is_preview_mode_changed: isPreviewModeChanged,
        isSnappingPoint: !!SLIDER_MARKS?.[currentWidth],
        viewportWidth: windowWidth,
        sliderMaxValue: maxWidth,
      });
    },
    [
      maxWidth,
      previewType,
      previousPreviewType,
      sendSliderValueOnPreviewChangeBi,
      windowWidth,
    ],
  );

  const debouncedSendSliderValueOnPreviewChangeBi = useMemo(
    () =>
      _.debounce((currentWidth: number) => {
        if (sliderStartValue.current !== currentWidth) {
          reportPreviewWidthChange(
            currentWidth,
            sliderStartValue.current,
            'slider',
          );
          sliderStartValue.current = currentWidth;
        }
      }, 500),
    [reportPreviewWidthChange],
  );

  useEffect(() => {
    function handleWindowResize(e: Event) {
      setWindowWidth((e.currentTarget as Window).innerWidth);
    }
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [previewType, previewWidth, windowWidth]);

  const handleSliderChange = (value: number) => {
    if (
      value &&
      previewType !== 'fullScreen' &&
      previewType !== 'largeScreen'
    ) {
      const newPreviewWidth = calcPreviewWidth(value, maxWidth, baselineWidth);
      setPreviewWidth(newPreviewWidth);
      debouncedSendSliderValueOnPreviewChangeBi(value);
    }
  };

  const mobileBtnClickHandler = () => {
    switchToMobile();
    const previousPreviewWidth = sliderStartValue.current;
    sliderStartValue.current = MOBILE_CANVAS_SIZE;
    reportTopBarClick('preview_mode_mobile');
    reportPreviewWidthChange(
      MOBILE_CANVAS_SIZE,
      previousPreviewWidth,
      'mobile_button',
    );
  };

  const desktopBtnClickHandler = () => {
    switchToDesktop();
    const previousPreviewWidth = sliderStartValue.current;
    sliderStartValue.current = DESKTOP_CANVAS_SIZE;
    reportTopBarClick('preview_mode_desktop');
    reportPreviewWidthChange(
      DESKTOP_CANVAS_SIZE,
      previousPreviewWidth,
      'desktop_button',
    );
  };

  return (
    <div
      className={util.cx(
        styles.topBarDeviceSwitchWithResize,
        'with-separators',
      )}
      data-aid="top-bar-device-switch-with-resize-section"
    >
      <div className={styles.fluidPreviewControls}>
        <Button
          automationId="top-bar-button-switch-mode-mobile"
          toggled={previewType === 'mobile'}
          symbolName="topBarMobile_NewWorkspace"
          className={styles.topBarBtnDevice}
          onClick={mobileBtnClickHandler}
          tooltip={TooltipContent({
            tooltipData: {
              title: 'Topbar_Preview_Mode_Mobile_Tooltip_Title',
              text: 'Topbar_Preview_Mode_Mobile_Tooltip_Body',
            },
          })}
        />
        <div
          className={util.cx(styles.deviceSwitchSlider, {
            [styles.hidden]: !shouldShowSlider,
          })}
        >
          <Slider
            value={previewWidth}
            onChange={(value) => handleSliderChange(value as number)}
            min={MOBILE_CANVAS_SIZE}
            max={maxWidth}
            displayMarks={false}
            marks={SLIDER_MARKS}
            onBeforeChange={disablePreviewResizeAnimation}
            dataHook="device-switch-slider"
          />
        </div>
        <Button
          automationId="top-bar-button-switch-mode-desktop"
          toggled={isDesktop}
          symbolName="topBarDesktop_NewWorkspace"
          className={styles.topBarBtnDevice}
          onClick={desktopBtnClickHandler}
          tooltip={TooltipContent({
            tooltipData: {
              title: 'Topbar_Preview_Mode_Desktop_Tooltip_Title',
              text: 'Topbar_Preview_Mode_Desktop_Tooltip_Body',
            },
          })}
        />
      </div>
      <Button
        automationId={selectedMenuItem.automationId}
        customSymbol={selectedMenuItem.icon}
        className={util.cx([
          styles.topBarBtnDevice,
          styles.topBarBtnFullScreen,
        ])}
        onClick={() => {
          if (selectedMenuItem.previewType === previewType) {
            desktopBtnClickHandler();
            return;
          }
          selectedMenuItem.onClick();
        }}
        toggled={selectedMenuItem.previewType === previewType}
      />
      <PopoverMenu
        triggerElement={
          <ChevronDown
            className={util.cx(styles.topBarMenu, {
              [styles.toggled]: isMenuOpen,
            })}
          />
        }
        showArrow={false}
        onHide={() => setIsMenuOpen(false)}
        onShow={() => {
          setIsMenuOpen(true);
          reportTopBarClick('preview_mode_dropdown');
        }}
        zIndex={1500}
        moveBy={{ y: 15 }}
        textSize="small"
        dataHook="device-switch-dropdown"
      >
        {dropDownMenuItems.map((item) => (
          <PopoverMenu.MenuItem
            key={item.text}
            text={item.text}
            prefixIcon={item.icon}
            dataHook={`device-switch-dropdown-${item.automationId}`}
            onClick={() => {
              setSelectedMenuItem(item);
              item.onClick();
              reportTopBarClick(item.biCategoryForDropdown);
            }}
          />
        ))}
      </PopoverMenu>
    </div>
  );
};

export default connect(DeviceSwitchWithResize);
